import React, { useRef, useState, useEffect } from "react";
import { Box } from "@mui/material";
import useEditorState from "../editor/hooks/useEditorState";
import EditorTopBar from "../editor/EditorTopBar";
import EditorToolbar from "../editor/EditorToolbar";
import EditorCanvas from "../editor/EditorCanvas";
import EditorPropertiesPanel from "../editor/EditorPropertiesPanel";
import { extractModelFields } from "../editor/modelUtils";
import { uploadMedia } from "../../services/mediaService";

/**
 * ModelEditor — éditeur de design type Canva simplifié.
 *
 * Props:
 *   mode            "model" | "design"   — étiquette et comportement de sauvegarde
 *   initialTitle    string
 *   initialElements array                — éléments pré-chargés (édition existante)
 *   initialCanvasBg string               — couleur de fond initiale
 *   initialCanvasSize { width, height }  — dimensions initiales du canvas
 *   onSave          (data) => void       — callback appelé lors de la sauvegarde
 */
export default function Editor({
    mode = "design",
    initialTitle = "Sans titre",
    initialElements = [],
    initialCanvasBg = "#ffffff",
    initialCanvasSize = { width: 1080, height: 1080 },
    initialDuration = "",
    initialPublishTo = [],
    initialStartDate = "",
    initialEndDate = "",
    onSave,
    onCreateNew,
    onDeleteCurrent,
}) {
    const {
        title, setTitle,
        elements, setElements,
        selectedId, setSelectedId,
        selectedEl,
        canvasBg, setCanvasBg,
        canvasSize, setCanvasSize,
        scaleRef,
        fileInputRef,
        addText, addRect, addCircle, addImage,
        updateEl, deleteSelected, duplicateSelected, changeOrder,
        handleElMouseDown, handleResizeMouseDown,
        undo, redo, canUndo, canRedo,
    } = useEditorState({
        mode,
        initialTitle,
        initialElements,
        initialCanvasBg,
        initialCanvasSize,
    });
    const [isSavingMedia, setIsSavingMedia] = useState(false);
    const [duration, setDuration] = useState(initialDuration);
    const [publishTo, setPublishTo] = useState(initialPublishTo);
    const [startDate, setStartDate] = useState(initialStartDate);
    const [endDate, setEndDate] = useState(initialEndDate);
    const pendingUploadsRef = useRef(new Map());
    const localPreviewUrlsRef = useRef(new Map());
    const uploadedUrlsRef = useRef(new Map());

    // ── Responsive canvas scaling ─────────────────────────────────────────────
    const containerRef = useRef(null);
    const [containerDims, setContainerDims] = useState({ w: 800, h: 600 });

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(([entry]) => {
            setContainerDims({
                w: entry.contentRect.width,
                h: entry.contentRect.height,
            });
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const PADDING = 48;
    const scale = Math.min(
        (containerDims.w - PADDING) / canvasSize.width,
        (containerDims.h - PADDING) / canvasSize.height,
        1
    );
    // Keep scaleRef in sync so drag/resize handlers use the correct scale
    scaleRef.current = scale;

    useEffect(() => {
        return () => {
            localPreviewUrlsRef.current.forEach((previewUrl) => {
                URL.revokeObjectURL(previewUrl);
            });
            localPreviewUrlsRef.current.clear();
            pendingUploadsRef.current.clear();
            uploadedUrlsRef.current.clear();
        };
    }, []);

    const handleSave = async () => {
        const pending = Array.from(pendingUploadsRef.current.entries());
        let nextElements = elements;

        if (pending.length > 0) {
            setIsSavingMedia(true);
            try {
                const uploadedByElementId = new Map();
                await Promise.all(pending.map(async ([elementId, entry]) => {
                    if (!entry?.file) return;
                    const uploaded = await uploadMedia(entry.file);
                    if (!uploaded?.url) throw new Error("URL media manquante");
                    uploadedByElementId.set(elementId, uploaded.url);
                }));

                uploadedByElementId.forEach((url, elementId) => {
                    uploadedUrlsRef.current.set(elementId, url);
                });

                pendingUploadsRef.current.clear();
            } catch (error) {
                console.error("Erreur upload media:", error);
                const message = error?.message || "Erreur pendant l'upload du fichier";
                alert(message);
                return false;
            } finally {
                setIsSavingMedia(false);
            }
        }

        nextElements = elements.map((el) => {
            if (el.type !== "image") return el;
            const uploadedUrl = uploadedUrlsRef.current.get(el.id);
            if (!uploadedUrl) return el;
            return { ...el, src: uploadedUrl, imageUrl: "" };
        });

        try {
            const saveResult = await onSave?.({
                title,
                elements: nextElements,
                canvasBg,
                canvasSize,
                mode,
                modelFields: extractModelFields(nextElements),
                duration: duration === "" ? undefined : Number(duration),
                publishTo,
                startDate: startDate || undefined,
                endDate: endDate || undefined,
            });
            if (saveResult === false) return false;
            return true;
        } catch (error) {
            console.error("Erreur sauvegarde:", error);
            return false;
        }
    };

    const handleCreateNew = async () => {
        const saved = await handleSave();
        if (!saved) return;
        await onCreateNew?.();
    };

    const handleDeleteFromMenu = async () => {
        await onDeleteCurrent?.();
    };

    const handleImageFileChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !selectedEl || selectedEl.type !== "image") return;

        const previewUrl = URL.createObjectURL(file);
        const previousPreview = localPreviewUrlsRef.current.get(selectedEl.id);
        if (previousPreview) {
            URL.revokeObjectURL(previousPreview);
        }

        localPreviewUrlsRef.current.set(selectedEl.id, previewUrl);
        pendingUploadsRef.current.set(selectedEl.id, { file });
        uploadedUrlsRef.current.delete(selectedEl.id);
        updateEl(selectedEl.id, { src: previewUrl, imageUrl: "" });
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#eef0f3", overflow: "hidden" }}>

            <EditorTopBar
                title={title}
                setTitle={setTitle}
                canvasSize={canvasSize}
                setCanvasSize={setCanvasSize}
                onSave={handleSave}
                onCreateNew={mode === "model" ? handleCreateNew : null}
                onDelete={onDeleteCurrent ? handleDeleteFromMenu : null}
                mode={mode}
                isSaving={isSavingMedia}
            />

            <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
                <EditorToolbar
                    onAddText={addText}
                    onAddRect={addRect}
                    onAddCircle={addCircle}
                    onAddImage={addImage}
                    onDelete={deleteSelected}
                    onDuplicate={duplicateSelected}
                    onMoveUp={() => changeOrder(1)}
                    onMoveDown={() => changeOrder(-1)}
                    hasSelection={!!selectedId}
                    onUndo={undo}
                    onRedo={redo}
                    canUndo={canUndo}
                    canRedo={canRedo}
                />

                <EditorCanvas
                    canvasSize={canvasSize}
                    canvasBg={canvasBg}
                    elements={elements}
                    selectedId={selectedId}
                    scale={scale}
                    containerRef={containerRef}
                    onDeselectAll={() => setSelectedId(null)}
                    onElementMouseDown={handleElMouseDown}
                    onResizeMouseDown={handleResizeMouseDown}
                />

                <EditorPropertiesPanel
                    mode={mode}
                    selectedEl={selectedEl}
                    fileInputRef={fileInputRef}
                    onFileChange={handleImageFileChange}
                    updateEl={updateEl}
                    canvasBg={canvasBg}
                    setCanvasBg={setCanvasBg}
                    canvasSize={canvasSize}
                    isUploadingMedia={isSavingMedia}
                    duration={duration}
                    setDuration={setDuration}
                    publishTo={publishTo}
                    setPublishTo={setPublishTo}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    endDate={endDate}
                    setEndDate={setEndDate}
                />
            </Box>
        </Box>
    );
}

